<!-- @prettier -->
<template>
	<div class="hours-operation-wrapper">
		<div
			class="hours-operation__basic-advanced-toggle col-sm-9 col-md-9 col-md-offset-3"
		>
			<MultipleToggle
				:value="hoursType"
				:options="hoursTypeOptions"
				:disabled="disabled"
				@onToggle="hoursTypeChange"
			/>
		</div>

		<div class="hours-operation col-md-8 col-lg-7 col-md-offset-3">
			<div class="hours-operation__header">
				<span class="hours-operation__header-item">Day</span>
				<span class="hours-operation__header-item">Open</span>
				<span class="hours-operation__header-item">Close</span>
			</div>
			<div class="hours-operation__content">
				<template v-if="hoursType === 'basic'">
					<div key="basic" class="hours-operation__content-basic">
						<HoursOfOperationTimeValues
							:hours-type="hoursType"
							:open-time.sync="hoursOfOperationValues[0].openTime.time"
							:close-time.sync="hoursOfOperationValues[0].closeTime.time"
							:disabled="disabled"
						/>
						<div class="hours-operation__breaks-container">
							<div class="hours-operation__breaks col-md-4">
								<HoursOfOperationBreaks
									v-if="
										hoursOfOperationValues[0].breaks &&
											hoursOfOperationValues[0].breaks.length
									"
									:breaks="hoursOfOperationValues[0].breaks"
									@remove-break="
										hoursOfOperationValues[0].breaks.splice($event, 1)
									"
									:disabled="disabled"
								/>
							</div>
							<div
								id="breaksDay0"
								class="col-md-8 hours-operation__breaks-wrapper"
							>
								<HoursOfOperationBreakForm
									@addBreak="addBreak($event.timeBreak, 0)"
									@toggleBreakForm="toggleBreakForm($event, 0)"
									:disabled="disabled"
								/>
							</div>
						</div>
					</div>
				</template>
				<template v-if="hoursType === 'advanced'">
					<div
						v-for="(hours, dayIndex) in hoursOfOperationValues"
						:key="`day-${dayIndex}`"
						class="hours-operation__content-advanced"
					>
						<HoursOfOperationTimeValues
							:hours-type="hoursType"
							:is-open.sync="hours.isOpen"
							:open-time.sync="hours.openTime.time"
							:close-time.sync="hours.closeTime.time"
							:day="days[dayIndex]"
							:disabled="disabled"
						/>
						<div class="hours-operation__breaks-container">
							<div class="hours-operation__breaks col-md-4">
								<HoursOfOperationBreaks
									v-if="hours.breaks && hours.breaks.length"
									:breaks="hours.breaks"
									@remove-break="hours.breaks.splice($event, 1)"
									:disabled="disabled"
								/>
							</div>
							<div
								:id="`breaksDay${dayIndex}`"
								class="col-md-8 hours-operation__breaks-wrapper"
							>
								<HoursOfOperationBreakForm
									:index="dayIndex"
									@addBreak="addBreak($event.timeBreak, dayIndex)"
									@toggleBreakForm="toggleBreakForm($event, dayIndex)"
									:disabled="disabled"
								/>
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import { isEqual } from 'lodash';
import { hoursOfOperation, days } from './constants';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';
import HoursOfOperationBreaks from './HoursOfOperationBreaks.vue';
import HoursOfOperationBreakForm from './HoursOfOperationBreakForm.vue';
import HoursOfOperationTimeValues from './HoursOfOperationTimeValues.vue';

export default {
	name: 'HoursOfOperation',
	components: {
		MultipleToggle,
		HoursOfOperationBreaks,
		HoursOfOperationBreakForm,
		HoursOfOperationTimeValues
	},
	props: {
		initialHoursOfOperation: {
			type: Array,
			default: () => []
		},
		disabled: {
			type: Boolean,
			default() {
				return false
			}
		}
	},
	data() {
		return {
			hoursType: 'basic',
			hoursOfOperationValues: _.cloneDeep(hoursOfOperation),
			days: Object.freeze(days)
		};
	},
	computed: {
		hoursTypeOptions() {
			return [
				{
					name: this.$t('BASIC_TEXT'),
					value: 'basic'
				},
				{
					name: this.$t('ADVANCED_TEXT'),
					value: 'advanced'
				}
			];
		}
	},
	created() {
		if (this.initialHoursOfOperation) {
			this.initializeHoursType(this.initialHoursOfOperation);
			this.initializeHoursOfOperation(this.initialHoursOfOperation);
			this.setHoursOfOperation(this.hoursOfOperationValues);
		} else {
			this.translateHoursOfOperation();
		}
	},
	watch: {
		hoursOfOperationValues: {
			handler: function() {
				this.translateHoursOfOperation();
			},
			deep: true
		}
	},
	methods: {
		hoursTypeChange(event) {
			this.hoursType = event.value;
			if (event.value === 'advanced') {
				// Deep clone the first element to apply to each day for advanced mode
				const updatedHoursOfOperationValues = this.days.map((day) => {
					const firstElementData = _.cloneDeep(this.hoursOfOperationValues[0]);
					return {
						...firstElementData,
						isClosed: false,
						isOpen: true,
						day: day
					};
				});

				this.hoursOfOperationValues = updatedHoursOfOperationValues;
			} else {
				// Handle other cases or direct mode switch without additional logic
				this.translateHoursOfOperation();
			}
		},
		initializeHoursType(hoursOfOperation) {
			if (hoursOfOperation && hoursOfOperation.length) {
				const holdValue = hoursOfOperation.every((day) => {
					if (isEqual(day, hoursOfOperation[0])) {
						return true;
					}
				});
				this.hoursType = holdValue ? 'basic' : 'advanced';
			} else {
				this.hoursType = 'basic';
			}
		},
		initializeHoursOfOperation(hoursOfOperation) {
			if (hoursOfOperation.length) {
			this.hoursOfOperationValues = hoursOfOperation.map((x, i) => {
				return {
					breaks: x.breaks ? x.breaks : [],
					day: this.days[i],
					openTime: {
						time: `${moment(x.openTime.hour, 'H').format('HH')}:${moment(
							x.openTime.minute,
							'm'
						).format('mm')}`,
						hour: moment(x.openTime.hour, 'H').format('HH'),
						minute: moment(x.openTime.minute, 'm').format('mm')
					},
					closeTime: {
						time: `${moment(x.closeTime.hour, 'H').format('HH')}:${moment(
							x.closeTime.minute,
							'm'
						).format('mm')}`,
						hour: moment(x.closeTime.hour, 'H').format('HH'),
						minute: moment(x.closeTime.minute, 'm').format('mm')
					},
					isClosed: x.isClosed,
					isOpen: !x.isClosed,
					inverted: x.inverted
				};
			});
			}
		},
		addBreak(timeBreak, index) {
			this.hoursOfOperationValues[index].breaks.push(timeBreak);
		},
		toggleBreakForm(value, index) {
			const breaksDay = document.getElementById('breaksDay' + index);
			if (value) {
				breaksDay.classList.add('open');
			} else {
				breaksDay.classList.remove('open');
			}
		},
		translateHoursOfOperation() {
			if (this.hoursType == 'basic') {
				const hoursOfOperation = this.hoursOfOperationValues.map(() => {
					return {
						breaks: this.hoursOfOperationValues[0].breaks,
						openTime: {
							hour: ~~moment(
								this.hoursOfOperationValues[0].openTime.time,
								'HH:mm'
							).format('H'),
							minute: ~~moment(
								this.hoursOfOperationValues[0].openTime.time,
								'HH:mm'
							).format('m')
						},
						closeTime: {
							hour: ~~moment(
								this.hoursOfOperationValues[0].closeTime.time,
								'HH:mm'
							).format('H'),
							minute: ~~moment(
								this.hoursOfOperationValues[0].closeTime.time,
								'HH:mm'
							).format('m')
						},
						inverted: this.hoursOfOperationValues[0].inverted,
						isClosed: false
					};
				});

				this.setHoursOfOperation(hoursOfOperation);
			} else if (this.hoursType == 'advanced') {
				const hoursOfOperation = this.hoursOfOperationValues.map((day) => {
					return {
						breaks: day.breaks,
						openTime: {
							hour: ~~moment(day.openTime.time, 'HH:mm').format('H'),
							minute: ~~moment(day.openTime.time, 'HH:mm').format('m')
						},
						closeTime: {
							hour: ~~moment(day.closeTime.time, 'HH:mm').format('H'),
							minute: ~~moment(day.closeTime.time, 'HH:mm').format('m')
						},
						inverted: day.inverted,
						isClosed: !day.isOpen
					};
				});

				this.setHoursOfOperation(hoursOfOperation);
			}
		},
		setHoursOfOperation(hoursOfOperationValues) {
			this.$emit('hoursOfOperation', hoursOfOperationValues);
		}
	}
};
</script>
